import { useRef } from "react"
import type React from "react"
import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    useTheme,
    useMediaQuery,
    Tooltip,
    IconButton,
    TextField,
    MenuItem,
} from "@mui/material"
import { container } from "../../infrastructure/container"
import type { SessionService } from "../../infrastructure/services/session"
import type { FranchiseService } from "../../infrastructure/services/api/franchise"
import type { OrderService } from "../../infrastructure/services/api/orderManagement"
import type { GetOrderResponse, OrderPaymentResponse, OrderStatusCountsByDate } from "../../infrastructure/models/transport/response/GetOrdersResponse"
import type { Franchise } from "../../infrastructure/models/common/franchise"
import { COLORS } from "../../presentation/constants/colors"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { useSearch } from "../../presentation/context/SearchContext"
import type { Logger } from "../../infrastructure/services/logger"
import { CalendarDays, Minus, Pencil, Plus } from "lucide-react"
import { ShoppingBag, Filter } from "lucide-react"
import { IndianRupee } from "lucide-react"
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore"
import ExpandLessIcon from "@mui/icons-material/ExpandMore"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import RefreshIcon from "@mui/icons-material/Refresh"
import { motion } from "framer-motion"
import Searchbar from "./Searchbar"
import { OrderStatus } from "../../infrastructure/models/common/orderStatus"
import { SupportRole } from "../../infrastructure/models/common/supportrole"
import { RefundService } from "../../infrastructure/services/api/refundAmount"
import { RefundPaymentRequest } from "../../infrastructure/models/transport/request/RefundBodyRequest"
import { RefundReason } from "../../presentation/models/enum/refundReason"

type DateRange = [Date | null, Date | null]

const formatToIST = (dateInput: string | Date | null | undefined): string => {
    if (!dateInput) return "-"

    const date = new Date(dateInput)
    if (isNaN(date.getTime())) return "-"

    const fmt = new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        timeZone: "Asia/Kolkata",
    })

    const parts = fmt.formatToParts(date)
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ""
    const day = get("day")
    const month = get("month")
    const year = get("year")

    return `${day} ${month} ${year}`
}

const formatUTCFixed = (dateInput: string | Date): string => {
    const date = new Date(dateInput)

    const fmt = new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata"
    })

    const parts = fmt.formatToParts(date)
    const get = (type: string) => parts.find(p => p.type === type)?.value ?? ""

    const day = get("day")
    const month = get("month")
    const year = get("year")
    const hour = get("hour")
    const minute = get("minute")
    const dayPeriod = get("dayPeriod")

    return `${day} ${month} ${year}, ${hour}:${minute} ${dayPeriod}`
}

const formatBackendTime = (dateInput: string | Date): string => {
    const date = new Date(dateInput)

    const fmt = new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata"
    })

    // format returns something like "06 Nov 2025, 12:45 PM" in many environments
    const parts = fmt.formatToParts(date)
    const get = (type: string) => parts.find(p => p.type === type)?.value ?? ""

    const hour = get("hour")
    const minute = get("minute")
    const dayPeriod = get("dayPeriod")

    return `${hour}:${minute} ${dayPeriod}`
}

const FranchiseOrders: React.FC = () => {
    const { franchiseId } = useParams()
    const sessionService = container.resolve<SessionService>("sessionService")
    const franchiseService = container.resolve<FranchiseService>("franchiseService")
    const orderService = container.resolve<OrderService>("orderService")
    const refundService = container.resolve<RefundService>("refundService")
    const [loadingRefundId, setLoadingRefundId] = useState<string | null>(null)
    const [loadingRefundStatusId, setLoadingRefundStatusId] = useState<string | null>(null)
    const fetchedRefundOperations = useRef<Set<string>>(new Set())
    const navigate = useNavigate()
    const { searchQuery } = useSearch()
    const [franchise, setFranchise] = useState<Franchise | null>(null)
    const [terminals, setTerminals] = useState<any[]>([])
    const [orders, setOrders] = useState<GetOrderResponse[]>([])
    const [error, setError] = useState<string | null>(null)
    const [apiLoading, setApiLoading] = useState(false)
    const logger = container.resolve<Logger>("logger")

    const [currentPage, setCurrentPage] = useState<number>(1)
    const [totalOrders, setTotalOrders] = useState<number>(0)
    const [totalRevenue, setTotalRevenue] = useState<number>(0)
    const [deductedAmount, setDeductedAmount] = useState<number>(0)
    const [totalRevenueAfterDeduction, setTotalRevenueAfterDeduction] = useState<number>(0)
    const [limit, setLimit] = useState<number>(350)

    // Store session data in local state to prevent unnecessary re-renders
    const [franchises, setFranchises] = useState(sessionService.franchises)
    const [allTerminals, setAllTerminals] = useState(sessionService.terminals)
    const [selectedTerminalId, setSelectedTerminalId] = useState<string>("")
    const [selectedDate, setSelectedDate] = useState<Date | null>(null)
    const [selectedOrder, setSelectedOrder] = useState<GetOrderResponse | null>(null)
    const [statusCounts, setStatusCounts] = useState<OrderStatusCountsByDate[]>([])
    const [month, setMonth] = useState<string>("")
    const [refundStatusMap, setRefundStatusMap] = useState<Record<string, string>>({})

    const [rangeStart, setRangeStart] = useState<Date | null>(null)
    const [rangeEnd, setRangeEnd] = useState<Date | null>(null)
    const [rangeValue, setRangeValue] = useState<DateRange>([null, null])
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const [expandedDate, setExpandedDate] = useState<string | null>(null)
    const [expandedAll, setExpandedAll] = useState<boolean>(false)
    const [showFilterDropdown, setShowFilterDropdown] = useState<boolean>(false)
    const [selectedStatus, setSelectedStatus] = useState<string>("all")
    const [allOrders, setAllOrders] = useState<GetOrderResponse[]>([])
    const [editingOrderId, setEditingOrderId] = useState<string | null>(null)
    const [editingStatus, setEditingStatus] = useState<string>("")
    const [pendingStatusChange, setPendingStatusChange] = useState<{ orderId: string | null, newStatus: string | null }>({
        orderId: null,
        newStatus: null
    })
    const currentUser = sessionService.user
    const nonEditableStatuses = ["completed", "canceled", "refunded"]
    const [refunded, setRefunded] = useState<number>(0)
    const [customRefundReason, setCustomRefundReason] = useState<string>("")
    const [refundReason, setRefundReason] = useState<RefundReason>(RefundReason.REQUESTED_BY_CUSTOMER)
    const [updateSuccessOpen, setUpdateSuccessOpen] = useState(false)
    const [openRefundFor, setOpenRefundFor] = useState<string | null>(null)
    const isOrderRefunded = selectedOrder?.payments?.some((pay) => !!pay.refundOperationId) || false

    const allowedRolesForRevenue = [
        SupportRole.SUPERADMIN,
        SupportRole.ADMIN,
        SupportRole.MANAGER
    ]

    const canViewRevenue = allowedRolesForRevenue.includes(currentUser?.role)

    const [sortConfig, setSortConfig] = useState<{
        key: string
        direction: "asc" | "desc"
    } | null>(null)

    const totalPages = Math.ceil(totalOrders / limit)

    const clearDateFilter = () => {
        setSelectedDate(null)
    }

    const clearAllFilters = () => {
        setSelectedStatus("all")
        setSelectedDate(null)
        setSelectedTerminalId("")
        setRangeStart(null)
        setRangeEnd(null)
        setRangeValue([null, null])
    }

    const handleSort = (key: string) => {
        let direction: "asc" | "desc" = "asc"
        if (sortConfig && sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc"
        }
        setSortConfig({ key, direction })
    }

    const handlePageChange = (page: number) => {
        const next = Math.max(1, Math.min(totalPages || 1, page))
        setCurrentPage(next)
    }

    const handleLimitChange = (newLimit: number) => {
        setLimit(newLimit)
        setCurrentPage(1) // Reset to first page when changing limit
    }

    const toggleTableExpand = (date: string) => {
        setExpandedDate((prev) => (prev === date ? null : date))
    }

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    const normalizeStatus = (status: string): string => status.trim().toLowerCase().replaceAll("_", "-")

    const sizePriority: Record<string, number> = {
        small: 1,
        medium: 2,
        large: 3,
        x_l: 4,
        custom: 5,
    }

    const statusPriority1: Record<string, number> = {
        "in-progress": 1,
        completed: 2,
        pending: 3,
        "pickup-locker-pending": 4,
        canceled: 5,
    }

    useEffect(() => {
        if (rangeStart && rangeEnd) {
            setRangeValue([rangeStart, rangeEnd])
        } else {
            setRangeValue([null, null])
        }
    }, [rangeStart, rangeEnd])

    // Sync session data when it changes
    useEffect(() => {
        setFranchises(sessionService.franchises)
        setAllTerminals(sessionService.terminals)
    }, [])

    // Fetch franchise and related terminals when franchiseId changes
    useEffect(() => {
        if (!franchiseId) return
        const currentFranchise = sessionService.currentFranchise
        if (franchiseId) {
            setFranchise(currentFranchise)
            const franchiseTerminals = allTerminals.filter((t: any) => t.franchiseId === franchiseId)
            setTerminals(franchiseTerminals)
        } else {
            setError("Franchise not found.")
        }
    }, [franchiseId, franchises, allTerminals])

    const formatAddress = (address: any): string => {
        if (!address) return "N/A"

        if (typeof address === "string") return address

        const { street = "", city = "", state = "", postalCode = "" } = address
        return `${street}, ${city}, ${state} - ${postalCode}`
    }

    /* calender, searchbar and select dropdown filteration*/
    const filteredOrders = orders.filter((order) => {
        const terminalName = (franchise?.terminals as any[])?.find((t: any) => t.terminalId === order.terminal)?.name || ""
        const orderDateObj = order.dropOffTime ? new Date(order.dropOffTime) : null
        const orderDateStr = orderDateObj?.toISOString().split("T")[0] || null

        const matchesSearch =
            terminalName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            order.status?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            order.receiverMobile?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            order.lockerSize?.toLowerCase().includes(searchQuery.toLowerCase())

        const matchesDate =
            !selectedDate || (orderDateStr && new Date(orderDateStr).toDateString() === selectedDate.toDateString())

        const matchesTerminal = selectedTerminalId ? order.terminal === selectedTerminalId : true

        const matchesStatus = selectedStatus === "all" || order.status?.toLowerCase() === selectedStatus.toLowerCase()

        const fromDate = rangeValue[0] ? new Date(new Date(rangeValue[0]).setHours(0, 0, 0, 0)) : null

        const toDate = rangeValue[1] ? new Date(new Date(rangeValue[1]).setHours(23, 59, 59, 999)) : null
        const matchesRangeDate =
            !fromDate || !toDate || (orderDateObj && fromDate && toDate && orderDateObj >= fromDate && orderDateObj <= toDate)

        return matchesSearch && matchesDate && matchesTerminal && matchesRangeDate && matchesStatus
    })

    const grouped = filteredOrders.reduce((acc, order) => {
        if (!order?.dropOffTime) return acc

        const parsedDate = new Date(order.dropOffTime)
        if (isNaN(parsedDate.getTime())) return acc

        // Convert to IST safely
        const istFormatter = new Intl.DateTimeFormat("en-IN", {
            timeZone: "Asia/Kolkata",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        })

        const formattedDate = istFormatter.format(parsedDate)
        const [day, month, year] = formattedDate.split("/")
        const key = `${year}-${month}-${day}`

        if (!acc[key]) acc[key] = []
        acc[key].push(order)
        return acc
    }, {} as Record<string, GetOrderResponse[]>)

    const dates = Object.keys(grouped).sort((a, b) => new Date(b).getTime() - new Date(a).getTime())

    const fromDate = rangeValue[0] ? new Date(new Date(rangeValue[0]).setHours(0, 0, 0, 0)) : null

    const toDate = rangeValue[1] ? new Date(new Date(rangeValue[1]).setHours(23, 59, 59, 999)) : null

    const rangeTotal = orders
        .filter((order) => {
            const dropTime = new Date(order.dropOffTime)
            return fromDate && toDate && dropTime >= fromDate && dropTime <= toDate
        })
        .flatMap((order) => order.payments?.filter((p) => p.status.toLowerCase() === "approved") || [])
        .reduce((sum, p) => sum + p.amount, 0)

    const [dateWiseTotal, setDateWiseTotals] = useState<Record<string, number>>({})

    const dateWiseTotals: Record<string, number> = {}

    dates.forEach((dateStr) => {
        const paymentsForDate = grouped[dateStr].flatMap(
            (order) => order.payments?.filter((p) => p.status.toLowerCase() === "approved") || [],
        )
        dateWiseTotals[dateStr] = paymentsForDate.reduce((sum, p) => sum + p.amount, 0)
    })

    useEffect(() => {
        setFranchise(sessionService.currentFranchise)
        setTerminals(sessionService.terminals.filter((t) => t.franchiseId === franchiseId))
    }, [franchiseId, sessionService])

    useEffect(() => {
        const fetchOrders = async () => {
            if (!franchiseId) return
            setError(null)
            setApiLoading(true)
            try {
                // Pass page and limit parameters to the API call
                const res = await orderService.getOrderDetails(franchiseId, currentPage, limit)

                // Handle paginated response
                if (res && typeof res === "object" && "orders" in res) {
                    // If API returns paginated response with data, total, totalRevenue
                    setAllOrders(res.orders)
                    setOrders(res.orders || [])
                    setTotalOrders(res.total || 0)
                    setRefunded(res.refunded || 0)
                    setTotalRevenue(res.totalRevenue || 0)
                    setDeductedAmount(res.deductedAmount || 0)
                    setTotalRevenueAfterDeduction(res.totalRevenueAfterDeduction || 0)
                    setStatusCounts(res.statusCounts || [])
                    setMonth(res.month || "")
                } else {
                    // TODO: Evaluate this is still needed or not.
                    // If API returns array directly (fallback for non-paginated response)
                    setAllOrders(Array.isArray(res) ? res : [])
                    setOrders(Array.isArray(res) ? res : [])
                    setTotalOrders(Array.isArray(res) ? res.length : 0)
                    setStatusCounts([])
                    setMonth(res.month || "")
                }
            } catch (e) {
                setError("Failed to load orders")
            } finally {
                setApiLoading(false)
            }
        }
        fetchOrders()
    }, [franchiseId, currentPage, limit]) // Added currentPage and limit as dependencies

    useEffect(() => {
        const totals: Record<string, number> = {}
        allOrders.forEach(order => {
            const dateStr = new Date(order.dropOffTime).toISOString().split("T")[0]
            const approvedPayments = order.payments?.filter(p => p.status.toLowerCase() === "approved") || []
            const totalAmount = approvedPayments.reduce((sum, p) => sum + p.amount, 0)
            totals[dateStr] = (totals[dateStr] || 0) + totalAmount
        })
        setDateWiseTotals(totals)
    }, [allOrders])

    useEffect(() => {
        const fetchOrdersByRange = async () => {
            if (!franchiseId || !rangeStart || !rangeEnd) return

            setApiLoading(true)
            setError(null)

            try {
                const formatToISTLocal = (date: Date): string => {
                    const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000))
                    const year = istDate.getUTCFullYear()
                    const month = String(istDate.getUTCMonth() + 1).padStart(2, "0")
                    const day = String(istDate.getUTCDate()).padStart(2, "0")
                    return `${year}-${month}-${day}`
                }

                const startISO = formatToISTLocal(rangeStart)
                const endISO = formatToISTLocal(rangeEnd)

                const res = await orderService.getOrdersByDateRange(
                    franchiseId,
                    startISO,
                    endISO,
                    currentPage,
                    limit
                )

                if (res && typeof res === "object" && "orders" in res) {
                    setAllOrders(res.orders)
                    setOrders(res.orders || [])
                    setTotalOrders(res.total || 0)
                    setRefunded(res.refunded || 0)
                    setTotalRevenue(res.totalRevenue || 0)
                    setDeductedAmount(res.deductedAmount || 0)
                    setTotalRevenueAfterDeduction(res.totalRevenueAfterDeduction || 0)
                    setStatusCounts(res.statusCounts || [])
                    setMonth(res.month || "")
                } else {
                    setAllOrders(Array.isArray(res) ? res : [])
                    setOrders(Array.isArray(res) ? res : [])
                    setTotalOrders(Array.isArray(res) ? res.length : 0)
                }
            } catch (e) {
                setError("Failed to load orders for selected date range")
            } finally {
                setApiLoading(false)
                setShowFilterDropdown(false)
            }
        }

        fetchOrdersByRange()
    }, [rangeStart, rangeEnd, franchiseId, currentPage, limit])

    useEffect(() => {
        if (rangeStart && rangeEnd) {
            setCurrentPage(1)
        }
    }, [rangeStart, rangeEnd])

    const handleRefreshOrders = async () => {
        if (!franchiseId) return
        setApiLoading(true)
        setError(null)

        try {
            const res = await orderService.getOrderDetails(franchiseId, currentPage, limit)

            if (res && typeof res === "object" && "orders" in res) {
                setOrders(res.orders || [])
                setTotalOrders(res.total || 0)
                setRefunded(res.refunded || 0)
                setTotalRevenue(res.totalRevenue || 0)
                setDeductedAmount(res.deductedAmount || 0)
                setTotalRevenueAfterDeduction(res.totalRevenueAfterDeduction || 0)
            } else {
                setOrders(Array.isArray(res) ? res : [])
                setTotalOrders(Array.isArray(res) ? res.length : 0)
            }
        } catch (e) {
            setError("Failed to refresh orders")
        } finally {
            setApiLoading(false)
        }
    }

    const statusCountByDate: Record<
        string,
        {
            date: string
            total: number
            completed: number
            "in-progress": number
            pending: number
            canceled: number
            "pickup-locker-pending": number
        }
    > = {}

    statusCounts.forEach((sc) => {
        statusCountByDate[sc.date] = {
            date: sc.date,
            total: sc.totalCount || 0,
            completed: sc.counts.completed || 0,
            "in-progress": sc.counts["in-progress"] || 0,
            pending: sc.counts.pending || 0,
            canceled: sc.counts.canceled || 0,
            "pickup-locker-pending": sc.counts["pickup-locker-pending"] || 0,
        }
    })

    const allowedStatusesToUpdateFrom = ["pending", "in-progress", "pickup-locker-pending"]
    const allowedNewStatuses = ["completed", "canceled"]

    const updateOrderStatus = async (orderId: string, currentStatus: string, newStatus: string) => {
        // Normalize
        currentStatus = currentStatus.toLowerCase()
        newStatus = newStatus.toLowerCase()

        // Validate current status
        if (!allowedStatusesToUpdateFrom.includes(currentStatus)) {
            alert("You cannot update this order status.")
            return false
        }

        // Validate new status
        if (!allowedNewStatuses.includes(newStatus)) {
            alert("Please select a valid status.")
            return false
        }

        try {
            await orderService.updateOrderStatus(
                orderId,
                currentStatus as OrderStatus,
                newStatus as OrderStatus
            )

            await handleRefreshOrders()   // reload orders after update
            return true

        } catch (err) {
            console.error("UPDATE ERROR:", err)
            alert("Failed to update status")
            return false
        }
    }


    const handleRefund = async (paymentOrderId: string, amount: number) => {
        if (!selectedOrder) return

        setLoadingRefundId(paymentOrderId)
        try {
            const response = await refundService.refundPayment({
                merchantOrderId: selectedOrder.orderId,
                paymentOrderId,
                franchiseId: franchiseId!,
                amount,
                customRefundReason
            })

            console.log("Refund response:", response)

            // 1. Close the refund form
            setOpenRefundFor(null)

            // 2. Show success dialog
            setUpdateSuccessOpen(true)

            // 3. Refresh orders so refundOperationId appears on the payment
            const res = await orderService.getOrderDetails(franchiseId!, currentPage, limit)
            if (res && typeof res === "object" && "orders" in res) {
                const refreshedOrders = res.orders || []
                setAllOrders(refreshedOrders)
                setOrders(refreshedOrders)
                setTotalOrders(res.total || 0)
                setRefunded(res.refunded || 0)
                setTotalRevenue(res.totalRevenue || 0)
                setDeductedAmount(res.deductedAmount || 0)
                setTotalRevenueAfterDeduction(res.totalRevenueAfterDeduction || 0)
                setStatusCounts(res.statusCounts || [])
                setMonth(res.month || "")

                // 4. Update selectedOrder so the dialog reflects the new refundOperationId immediately
                const updatedSelected = refreshedOrders.find((o: any) => o.orderId === selectedOrder.orderId)
                if (updatedSelected) {
                    setSelectedOrder(updatedSelected)

                    // 5. Auto-fetch refund status for any new refundOperationId on this order
                    updatedSelected.payments?.forEach((p: any) => {
                        if (p.refundOperationId && !fetchedRefundOperations.current.has(p.refundOperationId)) {
                            fetchedRefundOperations.current.add(p.refundOperationId)
                            handleRefundStatus(p.refundOperationId)
                        }
                    })
                }
            }
        } catch (error: any) {
            console.error("Refund failed:", error?.response || error)
            alert(error?.response?.data?.message || "Refund failed")
        } finally {
            setLoadingRefundId(null)
        }
    }

    useEffect(() => {
        if (updateSuccessOpen) {
            const timer = setTimeout(() => {
                setUpdateSuccessOpen(false)
            }, 2000)
            return () => clearTimeout(timer)
        }
    }, [updateSuccessOpen])

    useEffect(() => {
        // Automatically fetch refund status for all payments with refundOperationId on page load
        orders.forEach(order => {
            order.payments?.forEach(p => {
                if (p.refundOperationId && !fetchedRefundOperations.current.has(p.refundOperationId)) {
                    console.log("[v0] Auto-fetching refund status for operation:", p.refundOperationId)
                    fetchedRefundOperations.current.add(p.refundOperationId)
                    // Call the API to get the actual status instead of defaulting to PENDING
                    handleRefundStatus(p.refundOperationId)
                }
            })
        })
    }, [orders])

    const extractRefundStatus = (response: unknown): string | null => {
        if (!response || typeof response !== "object") return null

        const obj = response as Record<string, any>

        return (
            obj.refundStatus ||
            obj.status ||
            obj.result?.refundStatus ||
            obj.result?.status ||
            null
        )?.toUpperCase()
    }

    const handleRefundStatus = async (orderId: string) => {
        try {
            setLoadingRefundStatusId(orderId)
            const response = await refundService.getRefundStatus(orderId)

            const status = extractRefundStatus(response)

            console.log("[v0] Refund status fetched for", orderId, "Status:", status)

            if (!status) return

            setRefundStatusMap(prev => ({
                ...prev,
                [orderId]: status
            }))
        } catch (error) {
            console.error("Error fetching refund status", error)
        } finally {
            setLoadingRefundStatusId(null)
        }
    }

    return (
        <Box>
            <Box className="mb-3 px-1">
                <div className="flex flex-col gap-6 mb-6">

                    {/* ===================== ROW 1 ===================== */}
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 w-full">

                        {/* LEFT SIDE (Month + Orders) */}
                        <div className="flex flex-wrap items-center gap-2">
                            {/* Current Month */}
                            <div className="bg-red-50 px-4 py-3 rounded-lg flex items-center gap-1 border border-blue-100">
                                <div className="p-2 bg-red-100 rounded-full">
                                    <CalendarDays className="w-5 h-5 text-red-600" />
                                </div>
                                <div>
                                    <span className="text-sm text-red-600 font-medium block">Current Month</span>
                                    <div className="text-xl font-bold text-red-800">
                                        <motion.span
                                            key={month}
                                            initial={{ y: 20, opacity: 0 }}
                                            animate={{ y: 0, opacity: 1 }}
                                            transition={{ delay: 0.3 }}
                                            className="block"
                                        >
                                            {month || "Loading..."}
                                        </motion.span>
                                    </div>
                                </div>
                            </div>

                            {/* Total Orders */}
                            <div className="bg-blue-50 px-4 py-3 rounded-lg flex items-center gap-3 border border-blue-100">
                                <div className="p-2 bg-blue-100 rounded-full">
                                    <ShoppingBag className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <span className="text-sm text-blue-600 font-medium block">Total Orders</span>
                                    <div className="text-xl font-bold text-blue-800">{totalOrders}</div>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT SIDE (Revenue Cards) */}
                        {canViewRevenue && (
                            <div className="flex flex-wrap items-center gap-1 md:justify-end">
                                {/* Total Revenue */}
                                <div className="bg-green-50 px-4 py-3 rounded-lg flex items-center gap-3 border border-green-100">
                                    <div className="p-2 bg-green-100 rounded-full">
                                        <IndianRupee className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div>
                                        <span className="text-sm text-green-600 font-medium block">Total Revenue</span>
                                        <div className="text-xl font-bold text-green-800">
                                            {Number(totalRevenue || 0).toLocaleString("en-IN")}
                                        </div>
                                    </div>
                                </div>

                                {/* Total Deducted */}
                                <div className="bg-green-50 px-4 py-3 rounded-lg flex items-center gap-3 border border-green-100">
                                    <div className="p-2 bg-green-100 rounded-full">
                                        <IndianRupee className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div>
                                        <span className="text-sm text-green-600 font-medium block">Total Deducted Amount</span>
                                        <div className="text-xl font-bold text-green-800">
                                            {Number(deductedAmount || 0).toLocaleString("en-IN")}
                                        </div>
                                    </div>
                                </div>

                                {/* After Deduction */}
                                <div className="bg-blue-50 px-4 py-3 rounded-lg flex items-center gap-3 border border-blue-100">
                                    <div className="p-2 bg-blue-100 rounded-full">
                                        <IndianRupee className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <span className="text-sm text-blue-600 font-medium block">Total Amount After Deduction</span>
                                        <div className="text-xl font-bold text-blue-800">
                                            {Number(totalRevenueAfterDeduction || 0).toLocaleString("en-IN")}
                                        </div>
                                    </div>
                                </div>

                                {/* Total Refunded */}
                                <div className="bg-red-50 px-4 py-3 rounded-lg flex items-center gap-3 border border-red-100">
                                    <div className="p-2 bg-red-100 rounded-full">
                                        <IndianRupee className="w-5 h-5 text-red-600" />
                                    </div>
                                    <div>
                                        <span className="text-sm text-red-600 font-medium block">Total Refunded</span>
                                        <div className="text-xl font-bold text-red-800">
                                            ₹ {Number(refunded || 0).toLocaleString("en-IN")}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ===================== ROW 2 ===================== */}
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 w-full">

                        {/* LEFT SIDE (Search + Refresh) */}
                        <div className="flex items-center gap-2">
                            <Searchbar />

                            <Tooltip title="Refresh Orders">
                                <IconButton
                                    onClick={handleRefreshOrders}
                                    sx={{
                                        backgroundColor: "white",
                                        borderRadius: 2,
                                        border: "1px solid #ccc",
                                        width: 40,
                                        height: 40,
                                        boxShadow: 1,
                                        "&:hover": { backgroundColor: COLORS.LIGHT_GRAY },
                                    }}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </div>

                        {/* RIGHT SIDE (Back + Filter + Limit) */}
                        <div className="flex items-center gap-2 relative">
                            {/* Active Filters Display */}
                            {(selectedStatus !== "all" || selectedDate || selectedTerminalId || rangeValue[0] || rangeValue[1]) && (
                                <div className="flex flex-wrap items-center gap-1">
                                    <span className="text-sm text-gray-600 font-sm">Active filters:</span>

                                    {/* Status Filter Chip */}
                                    {selectedStatus !== "all" && (
                                        <span className="flex items-center gap-1 px-1 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">
                                            Status: {selectedStatus}
                                            <button
                                                onClick={() => setSelectedStatus("all")}
                                                className="ml-1 text-blue-600 hover:text-blue-800 font-bold"
                                            >
                                                ✕
                                            </button>
                                        </span>
                                    )}

                                    {/* Terminal Filter Chip */}
                                    {selectedTerminalId && (
                                        <span className="flex items-center gap-1 px-1 py-1 bg-purple-100 text-purple-800 rounded text-sm font-medium">
                                            Terminal: {terminals.find((t) => t.terminalId === selectedTerminalId)?.name}
                                            <button
                                                onClick={() => setSelectedTerminalId("")}
                                                className="ml-1 text-purple-600 hover:text-purple-800 font-bold"
                                            >
                                                ✕
                                            </button>
                                        </span>
                                    )}

                                    {/* Date Filter Chip */}
                                    {selectedDate && (
                                        <span className="flex items-center gap-1 px-1 py-1 bg-orange-100 text-orange-800 rounded text-sm font-medium">
                                            Date: {selectedDate.toLocaleDateString()}
                                            <button
                                                onClick={() => setSelectedDate(null)}
                                                className="ml-1 text-orange-600 hover:text-orange-800 font-bold"
                                            >
                                                ✕
                                            </button>
                                        </span>
                                    )}

                                    {/* Date Range Filter Chip */}
                                    {(rangeValue[0] || rangeValue[1]) && (
                                        <span className="flex items-center gap-1 px-1 py-1 bg-green-100 text-green-800 rounded text-sm font-medium">
                                            Range: {rangeValue[0]?.toLocaleDateString()} - {rangeValue[1]?.toLocaleDateString()}
                                            <button
                                                onClick={() => {
                                                    // clear everything
                                                    clearAllFilters()
                                                }}
                                                className="ml-1 text-green-600 hover:text-green-800 font-bold"
                                            >
                                                ✕
                                            </button>
                                        </span>
                                    )}


                                    {/* Clear All Chip */}
                                    <button
                                        onClick={clearAllFilters}
                                        className="px-1 py-1 bg-gray-200 text-gray-700 rounded text-sm font-medium hover:bg-gray-300"
                                    >
                                        Clear All ✕
                                    </button>
                                </div>
                            )}
                            <Button variant="contained" color="secondary" onClick={() => navigate(-1)}>
                                Back
                            </Button>

                            {/* Filter Button */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                                    className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:border-blue-500 font-medium text-sm transition-colors"
                                >
                                    <Filter />
                                    <svg className="fill-current h-5 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                    </svg>
                                </button>

                                {/* Filter Dropdown */}
                                {showFilterDropdown && (
                                    <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                                        <div className="p-4 space-y-4">
                                            <div className="flex justify-between items-center">
                                                <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
                                                <button
                                                    onClick={() => setShowFilterDropdown(false)}
                                                    className="text-gray-500 hover:text-gray-700 font-medium"
                                                >
                                                    ✕
                                                </button>
                                            </div>

                                            {/* Status Filter */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                                <select
                                                    value={selectedStatus}
                                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm font-medium"
                                                >
                                                    <option value="all">All Status</option>
                                                    <option value="pending">Pending</option>
                                                    <option value="in-progress">In Progress</option>
                                                    <option value="completed">Completed</option>
                                                    <option value="canceled">Canceled</option>
                                                    <option value="pickup-locker-pending">Pickup Locker Pending</option>
                                                </select>
                                            </div>

                                            {/* Terminal Filter */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Terminal</label>
                                                <select
                                                    value={selectedTerminalId}
                                                    onChange={(e) => setSelectedTerminalId(e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm font-medium"
                                                >
                                                    <option value="">All Terminals</option>
                                                    {(franchise?.terminals || []).map((terminal: any) => (
                                                        <option key={terminal.terminalId} value={terminal.terminalId}>
                                                            {terminal.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Date Range Filter */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <div className="flex gap-2">
                                                        <DatePicker
                                                            label="From"
                                                            value={rangeStart}
                                                            onChange={(newValue) => setRangeStart(newValue)}
                                                            disableFuture
                                                            slotProps={{
                                                                textField: {
                                                                    size: "small",
                                                                    sx: {
                                                                        width: "100%",
                                                                        backgroundColor: "white",
                                                                        "& .MuiInputBase-input": {
                                                                            fontSize: "0.875rem",
                                                                            fontWeight: 500,
                                                                        },
                                                                    },
                                                                },
                                                            }}
                                                        />
                                                        <DatePicker
                                                            label="To"
                                                            value={rangeEnd}
                                                            onChange={(newValue) => setRangeEnd(newValue)}
                                                            disableFuture
                                                            slotProps={{
                                                                textField: {
                                                                    size: "small",
                                                                    sx: {
                                                                        width: "100%",
                                                                        backgroundColor: "white",
                                                                        "& .MuiInputBase-input": {
                                                                            fontSize: "0.875rem",
                                                                            fontWeight: 500,
                                                                        },
                                                                    },
                                                                },
                                                            }}
                                                        />
                                                    </div>
                                                </LocalizationProvider>
                                            </div>

                                            {/* Date Filter */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <DatePicker
                                                        value={selectedDate}
                                                        onChange={(newValue) => setSelectedDate(newValue)}
                                                        disableFuture
                                                        slotProps={{
                                                            textField: {
                                                                size: "small",
                                                                sx: {
                                                                    width: "100%",
                                                                    backgroundColor: "white",
                                                                    "& .MuiInputBase-input": {
                                                                        fontSize: "0.875rem",
                                                                        fontWeight: 500,
                                                                    },
                                                                },
                                                            },
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            </div>

                                            {/* Filter Actions */}
                                            <div className="flex gap-2 pt-2">
                                                <button
                                                    onClick={clearAllFilters}
                                                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium transition-colors"
                                                >
                                                    Clear All
                                                </button>
                                                <button
                                                    onClick={() => setShowFilterDropdown(false)}
                                                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium transition-colors"
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <TextField
                                    select
                                    fullWidth
                                    label="Limit"
                                    name="status"
                                    value={limit}
                                    onChange={(e) => handleLimitChange(Number(e.target.value))}
                                >
                                    <MenuItem value={350}>350</MenuItem>
                                    <MenuItem value={500}>500</MenuItem>
                                    <MenuItem value={1000}>1000</MenuItem>
                                    <MenuItem value={1500}>1500</MenuItem>
                                </TextField>
                            </div>
                        </div>
                    </div>
                </div>
            </Box>

            {/* Filter Logic */}
            {!expandedAll ? (
                <div>
                    {(() => {
                        const statusPriority = {
                            "in-progress": 0,
                            pending: 1,
                            completed: 2,
                            "pickup-locker-pending": 3,
                            canceled: 4,
                        }

                        return apiLoading ? (
                            <CircularProgress />
                        ) : error ? (
                            <Typography color="error">{error}</Typography>
                        ) : dates.length === 0 ? (
                            <Box display="flex" justifyContent="center" alignItems="center">
                                <Typography className="text-gray-600" variant="h6">
                                    No orders found.
                                </Typography>
                            </Box>
                        ) : (
                            dates.map((date) => (
                                <Box key={date} mb={4} className="shadow-lg rounded-lg overflow-hidden bg-white">
                                    <Box
                                        className="pl-4 pr-4 pt-1 mb-1"
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        sx={{ columnGap: 2, overflowX: "auto", flexWrap: "nowrap", whiteSpace: "nowrap" }}
                                    >
                                        {/* Date */}
                                        <Box sx={{ width: 150 }}>
                                            <Typography variant="h6" sx={{ fontSize: "16px" }}>
                                                <strong>
                                                    {formatToIST(statusCountByDate[date]?.date || date)}
                                                </strong>
                                            </Typography>
                                        </Box>

                                        {/* Total Orders */}
                                        <Box sx={{ width: 820 }} display="flex" justifyContent="space-between" alignItems="center">
                                            <Typography variant="h6">
                                                <span style={{ fontSize: "16px" }}>
                                                    <strong style={{ color: COLORS.BLACK }}>Total Orders: {statusCountByDate[date]?.total || 0} </strong>
                                                </span>
                                                &nbsp;|&nbsp;
                                                {/* Completed */}
                                                <span style={{ fontSize: "16px" }}>
                                                    <strong style={{ color: COLORS.GREEN }}>
                                                        Completed:{" "}
                                                        {statusCountByDate[date]?.completed || 0}{" "}
                                                    </strong>
                                                </span>
                                                &nbsp;|&nbsp;
                                                {/* In-Progress */}
                                                <span style={{ fontSize: "16px" }}>
                                                    <strong style={{ color: COLORS.KORMA }}>
                                                        In-Progress:{" "}
                                                        {statusCountByDate[date]?.["in-progress"] || 0}{" "}
                                                    </strong>
                                                </span>
                                                &nbsp;|&nbsp;
                                                {/* Pending */}
                                                <span style={{ fontSize: "16px" }}>
                                                    <strong style={{ color: COLORS.DARK_SAFFRON }}>
                                                        Pending: {statusCountByDate[date]?.pending || 0}{" "}
                                                    </strong>
                                                </span>
                                                &nbsp;|&nbsp;
                                                <span style={{ fontSize: "16px" }}>
                                                    <strong style={{ color: COLORS.DOVEGRAY }}>
                                                        Canceled: {grouped[date]?.filter((o) => o.status.toLowerCase() === "canceled").length ||
                                                            0}{" "}
                                                    </strong>
                                                </span>
                                                &nbsp;|&nbsp;
                                                <span style={{ fontSize: "16px" }}>
                                                    <strong style={{ color: COLORS.PICKUPPENDING }}>
                                                        Pickup-Locker-Pending:{" "}
                                                        {grouped[date]?.filter((o) => o.status.toLowerCase() === "pickup-locker-pending").length ||
                                                            0}{" "}
                                                    </strong>
                                                </span>
                                            </Typography>
                                        </Box>

                                        {/* Total Amount */}
                                        <Box sx={{ width: 200 }}>
                                            <Typography variant="h6" sx={{ fontSize: "16px", color: COLORS.BLACK }}>
                                                <strong>
                                                    Total Amount: ₹{" "}
                                                    {(() => {
                                                        let total = 0;
                                                        let excludedTotal = 0;

                                                        grouped[date].forEach(order => {
                                                            if (!Array.isArray(order.payments)) return;

                                                            const approvedTotal = order.payments
                                                                .filter(
                                                                    p =>
                                                                        ["approved", "completed"].includes(p.status?.toLowerCase()) &&
                                                                        !p.refundOperationId
                                                                )
                                                                .reduce((sum, p) => sum + (p.amount || 0), 0);

                                                            const refundedTotal = order.payments
                                                                .filter(p => p.refundOperationId)
                                                                .reduce((sum, p) => sum + (p.amount || 0), 0);

                                                            const finalPayment = Math.max(approvedTotal - refundedTotal, 0);

                                                            total += finalPayment;

                                                            if (order.status?.toLowerCase() === "rejected") {
                                                                excludedTotal += finalPayment;
                                                            }
                                                        });

                                                        return (total - excludedTotal).toFixed(2);
                                                    })()}
                                                </strong>
                                            </Typography>

                                        </Box>

                                        {/* Expand/Collapse Button */}
                                        <Box sx={{ width: 20, textAlign: "center" }}>
                                            <button
                                                onClick={() => toggleTableExpand(date)}
                                                style={{
                                                    cursor: "pointer",
                                                    fontSize: "16px",
                                                    color: expandedDate === date ? COLORS.RED : COLORS.ACTIVE,
                                                    background: "transparent",
                                                    border: "none",
                                                }}
                                                title={expandedDate === date ? "Collapse Table" : "Expand Table"}
                                            >
                                                {expandedDate === date ? <Minus size={24} /> : <Plus size={24} />}
                                            </button>
                                        </Box>
                                    </Box>

                                    {expandedDate === date && (
                                        <Box className="overflow-x-auto px-2 pb-2">
                                            <table className="min-w-full text-sm text-left">
                                                <thead className="bg-gray-300">
                                                    <tr className="text-center">
                                                        <th className="px-4 py-1 whitespace-nowrap">PHONE NUMBER</th>
                                                        <th className="px-4 py-1">TERMINAL</th>
                                                        <th className="px-4 py-1 cursor-pointer" onClick={() => handleSort("locker")}>
                                                            LOCKER
                                                            {sortConfig?.key === "locker" ? (
                                                                sortConfig.direction === "asc" ? (
                                                                    <ExpandLessIcon />
                                                                ) : (
                                                                    <ExpandMoreIcon />
                                                                )
                                                            ) : (
                                                                <UnfoldMoreIcon />
                                                            )}
                                                        </th>
                                                        <th className="px-4 py-1 cursor-pointer" onClick={() => handleSort("status")}>
                                                            STATUS
                                                            {sortConfig?.key === "status" ? (
                                                                sortConfig.direction === "asc" ? (
                                                                    <ExpandLessIcon />
                                                                ) : (
                                                                    <ExpandMoreIcon />
                                                                )
                                                            ) : (
                                                                <ExpandMoreIcon />
                                                            )}
                                                        </th>
                                                        <th className="px-4 py-1">LOCKER PRICE</th>
                                                        <th className="px-4 py-1 cursor-pointer" onClick={() => handleSort("size")}>
                                                            SIZE
                                                            {sortConfig?.key === "size" ? (
                                                                sortConfig.direction === "asc" ? (
                                                                    <ExpandLessIcon />
                                                                ) : (
                                                                    <ExpandMoreIcon />
                                                                )
                                                            ) : (
                                                                <UnfoldMoreIcon />
                                                            )}
                                                        </th>
                                                        <th className="px-4 py-1 cursor-pointer" onClick={() => handleSort("dropOff")}>
                                                            DROP OFF
                                                            {sortConfig?.key === "dropOff" ? (
                                                                sortConfig.direction === "asc" ? (
                                                                    <ExpandLessIcon />
                                                                ) : (
                                                                    <ExpandMoreIcon />
                                                                )
                                                            ) : (
                                                                <UnfoldMoreIcon />
                                                            )}
                                                        </th>
                                                        <th className="px-4 py-1 cursor-pointer" onClick={() => handleSort("pickUp")}>
                                                            PICK UP
                                                            {sortConfig?.key === "pickUp" ? (
                                                                sortConfig.direction === "asc" ? (
                                                                    <ExpandLessIcon />
                                                                ) : (
                                                                    <ExpandMoreIcon />
                                                                )
                                                            ) : (
                                                                <UnfoldMoreIcon />
                                                            )}
                                                        </th>
                                                        <th className="px-4 py-1">FINAL PAYMENT</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {[...grouped[date]]
                                                        .sort((a, b) => {
                                                            const dir = sortConfig?.direction === "desc" ? -1 : 1

                                                            if (!sortConfig) {
                                                                const aPriority = statusPriority[normalizeStatus(a.status.toLowerCase())] ?? 99
                                                                const bPriority = statusPriority[normalizeStatus(b.status.toLowerCase())] ?? 99
                                                                return aPriority - bPriority
                                                            }

                                                            const { key } = sortConfig

                                                            if (key === "locker") return dir * (a.lockerNumber - b.lockerNumber)

                                                            if (key === "status") {
                                                                const aStatus = statusPriority1[normalizeStatus(a.status.toLowerCase())] ?? 99
                                                                const bStatus = statusPriority1[normalizeStatus(b.status.toLowerCase())] ?? 99
                                                                return dir * (aStatus - bStatus)
                                                            }

                                                            if (key === "size") {
                                                                const aKey = a.lockerSize.toLowerCase()
                                                                const bKey = b.lockerSize.toLowerCase()
                                                                const aPriority = sizePriority[aKey] ?? 99
                                                                const bPriority = sizePriority[bKey] ?? 99
                                                                return dir * (aPriority - bPriority)
                                                            }

                                                            if (key === "dropOff")
                                                                return dir * (new Date(a.dropOffTime).getTime() - new Date(b.dropOffTime).getTime())

                                                            if (key === "pickUp") {
                                                                if (!a.pickUpTime) return 1
                                                                if (!b.pickUpTime) return -1
                                                                return dir * (new Date(a.pickUpTime).getTime() - new Date(b.pickUpTime).getTime())
                                                            }

                                                            return 0
                                                        })

                                                        .map((order) => {
                                                            // Highlight the entire row if the order's status is "refunded"
                                                            const isRefundedOrder = order.status?.toLowerCase() === "refunded"

                                                            return (
                                                                <tr
                                                                    key={order.orderId}
                                                                    className={`border-b hover:bg-gray-50 text-center ${order.status.toLowerCase() === "rejected"
                                                                            ?  "bg-red-100 border-l-4 border-red-600"
                                                                            : isRefundedOrder
                                                                                ? "bg-yellow-100"
                                                                                : ""
                                                                        }`}
                                                                >
                                                                    <td
                                                                        className="px-4 py-1 whitespace-nowrap text-blue-600 font-bold cursor-pointer underline"
                                                                        onClick={() => setSelectedOrder(order)}
                                                                    >
                                                                        {order.receiverMobile}
                                                                    </td>

                                                                    <td className="px-4 py-1">
                                                                        {(franchise?.terminals as any[])?.find((t: any) => t.terminalId === order.terminal)?.name || order.terminal}
                                                                    </td>

                                                                    <td className="px-4 py-1">{order.lockerNumber}</td>

                                                                    <td className="px-4 py-1 relative">

                                                                        {editingOrderId === order.orderId &&
                                                                            [SupportRole.SUPERADMIN, SupportRole.ADMIN].includes(currentUser?.role) ? (
                                                                            <div className="flex items-center gap-2">

                                                                                {/* STATUS DROPDOWN */}
                                                                                <select
                                                                                    value={
                                                                                        pendingStatusChange.orderId === order.orderId
                                                                                            ? pendingStatusChange.newStatus || ""
                                                                                            : ""
                                                                                    }
                                                                                    autoFocus
                                                                                    onChange={(e) => {
                                                                                        setPendingStatusChange({
                                                                                            orderId: order.orderId,
                                                                                            newStatus: e.target.value
                                                                                        })
                                                                                    }}
                                                                                    className="px-2 py-1 border border-gray-300 rounded-md text-sm outline-none"
                                                                                >
                                                                                    <option value="">Select Status</option>

                                                                                    {allowedNewStatuses.map((s) => (
                                                                                        <option key={s} value={s}>
                                                                                            {s.replace(/-/g, " ")}
                                                                                        </option>
                                                                                    ))}
                                                                                </select>

                                                                                {/* SAVE BUTTON */}
                                                                                <button
                                                                                    className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                                                                                    disabled={!pendingStatusChange.newStatus}
                                                                                    onClick={async () => {
                                                                                        const newStatus = pendingStatusChange.newStatus

                                                                                        if (!newStatus) return

                                                                                        const success = await updateOrderStatus(
                                                                                            order.orderId,
                                                                                            order.status.toLowerCase(),    // current status
                                                                                            newStatus                       // new status
                                                                                        )

                                                                                        if (success) {
                                                                                            setEditingOrderId(null)
                                                                                        }

                                                                                        setPendingStatusChange({ orderId: null, newStatus: null })
                                                                                    }}
                                                                                >
                                                                                    Save
                                                                                </button>

                                                                                {/* CANCEL BUTTON */}
                                                                                <button
                                                                                    className="px-3 py-1 bg-gray-300 text-black rounded-md text-sm hover:bg-gray-400"
                                                                                    onClick={() => {
                                                                                        setEditingOrderId(null)
                                                                                        setPendingStatusChange({ orderId: null, newStatus: null })
                                                                                    }}
                                                                                >
                                                                                    Cancel
                                                                                </button>

                                                                            </div>
                                                                        ) : (

                                                                            /* NORMAL STATUS BADGE */
                                                                            <span
                                                                                onClick={() => {
                                                                                    // Block edit for completed, canceled, refunded
                                                                                    if (nonEditableStatuses.includes(order.status.toLowerCase())) return

                                                                                    // Allow edit only for superadmin/admin
                                                                                    if (![SupportRole.SUPERADMIN, SupportRole.ADMIN].includes(currentUser?.role)) return

                                                                                    setEditingOrderId(order.orderId)
                                                                                    setPendingStatusChange({ orderId: order.orderId, newStatus: null })
                                                                                }}
                                                                                className={`inline-flex items-center gap-1 px-2 py-1 font-semibold rounded-full
                                                                                            ${nonEditableStatuses.includes(order.status.toLowerCase()) ||
                                                                                        ![SupportRole.SUPERADMIN, SupportRole.ADMIN].includes(currentUser?.role)
                                                                                        ? "cursor-default"  // disabled look
                                                                                        : "cursor-pointer hover:brightness-90"
                                                                                    }
                                                                                        `}
                                                                                style={{
                                                                                    backgroundColor:
                                                                                        order.status.toLowerCase() === "completed"
                                                                                            ? COLORS.SCANDAL
                                                                                            : order.status.toLowerCase() === "pending"
                                                                                                ? COLORS.LIGHT_SAFFRON
                                                                                                : order.status.toLowerCase() === "in-progress"
                                                                                                    ? COLORS.BEESWAX
                                                                                                    : order.status.toLowerCase() === "pickup-locker-pending"
                                                                                                        ? COLORS.PICKUPPENDING1
                                                                                                        : order.status.toLowerCase() === "refunded"
                                                                                                            ? COLORS.EARLYDAWN
                                                                                                            : COLORS.GALLERY,
                                                                                    color:
                                                                                        order.status.toLowerCase() === "completed"
                                                                                            ? COLORS.JEWEL
                                                                                            : order.status.toLowerCase() === "pending"
                                                                                                ? COLORS.DARK_SAFFRON
                                                                                                : order.status.toLowerCase() === "in-progress"
                                                                                                    ? COLORS.KORMA
                                                                                                    : order.status.toLowerCase() === "pickup-locker-pending"
                                                                                                        ? COLORS.PICKUPPENDING
                                                                                                        : order.status.toLowerCase() === "refunded"
                                                                                                            ? COLORS.SEABUCKTHORN
                                                                                                            : COLORS.DOVEGRAY,
                                                                                }}
                                                                            >
                                                                                <span>{order.status}</span>
                                                                                {[SupportRole.SUPERADMIN, SupportRole.ADMIN].includes(currentUser?.role) &&
                                                                                    !nonEditableStatuses.includes(order.status.toLowerCase()) && (
                                                                                        <Pencil size={12} className="mt-[2px]" />
                                                                                    )}
                                                                            </span>
                                                                        )}
                                                                    </td>

                                                                    <td className="px-4 py-1">₹ {order.lockerPrice}</td>
                                                                    <td className="px-4 py-1">{order.lockerSize}</td>

                                                                    <td className="px-4 py-1">
                                                                        {order.dropOffTime ? formatBackendTime(order.dropOffTime) : "-"}
                                                                    </td>

                                                                    <td className="px-4 py-1">
                                                                        {order.pickUpTime ? formatBackendTime(order.pickUpTime) : "-"}
                                                                    </td>

                                                                    <td>
                                                                        {order.payments?.length ? (() => {

                                                                            const approvedTotal = order.payments
                                                                                .filter(p =>
                                                                                    ["approved", "completed"].includes(p.status?.toLowerCase()) &&
                                                                                    !p.refundOperationId
                                                                                )
                                                                                .reduce((sum, p) => sum + (p.amount || 0), 0)

                                                                            const refundedTotal = order.payments
                                                                                .filter(p => p.refundOperationId)
                                                                                .reduce((sum, p) => sum + (p.amount || 0), 0)

                                                                            const finalAmount = approvedTotal - refundedTotal

                                                                            return (
                                                                                <strong>
                                                                                    <span style={{ color: "green" }}>
                                                                                        ₹ {approvedTotal}
                                                                                    </span>

                                                                                    {" - "}

                                                                                    <span style={{ color: "red" }}>
                                                                                        ₹ {refundedTotal}
                                                                                    </span>

                                                                                    {" = "}

                                                                                    <span style={{ color: "blue" }}>
                                                                                        ₹ {finalAmount}
                                                                                    </span>
                                                                                </strong>
                                                                            )

                                                                        })() : "No Payments"}
                                                                    </td>

                                                                </tr>
                                                            )
                                                        })
                                                    }
                                                </tbody>
                                            </table>
                                        </Box>
                                    )}
                                    {selectedOrder && (
                                        <Dialog
                                            open={!!selectedOrder}
                                            onClose={() => setSelectedOrder(null)}
                                            maxWidth="md"
                                            fullWidth
                                            BackdropProps={{
                                                sx: {
                                                    backgroundColor: COLORS.TRANSPARANT_COLOR,
                                                },
                                            }}
                                            PaperProps={{
                                                sx: {
                                                    boxShadow: 8,
                                                    borderRadius: 2,
                                                },
                                            }}
                                        >
                                            <DialogTitle>Order Details</DialogTitle>

                                            <DialogContent
                                                dividers
                                                sx={{
                                                    maxHeight: "70vh",
                                                    overflowY: "auto",
                                                    px: 3,
                                                    py: 2,
                                                    backgroundColor: COLORS.WHITE,
                                                }}
                                            >
                                                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                                                    <Typography>
                                                        <strong>Order ID: </strong> {selectedOrder.orderId}
                                                    </Typography>
                                                    <Typography>
                                                        <strong>User ID: </strong> {selectedOrder.user || "-"}
                                                    </Typography>
                                                    <Typography>
                                                        <strong>Franchise: </strong> {franchise?.name || selectedOrder.franchiseId}
                                                    </Typography>
                                                    <Typography>
                                                        <strong>Terminal ID: </strong> {selectedOrder.terminal}
                                                    </Typography>
                                                    <Typography>
                                                        <strong>Terminal Name: </strong>{" "}
                                                        {(franchise?.terminals as any[])?.find((t: any) => t.terminalId === selectedOrder.terminal)
                                                            ?.name || selectedOrder.terminal}
                                                    </Typography>
                                                    <Typography>
                                                        <strong>Collect PIN: </strong> {selectedOrder.collectPin || "-"}
                                                    </Typography>
                                                    <Typography>
                                                        <strong>Locker Number: </strong> {selectedOrder.lockerNumber}
                                                    </Typography>
                                                    <Typography>
                                                        <strong>Locker Size: </strong> {selectedOrder.lockerSize}
                                                    </Typography>
                                                    <Typography>
                                                        <strong>Locker Price: </strong> ₹ {selectedOrder.lockerPrice}
                                                    </Typography>
                                                    <Typography>
                                                        <strong>Status: </strong>
                                                        <span
                                                            className="inline-block px-2 py-1 text-sm font-bold rounded-full"
                                                            style={{
                                                                backgroundColor:
                                                                    selectedOrder.status.toLowerCase() === "completed"
                                                                        ? COLORS.GREEN
                                                                        : selectedOrder.status.toLowerCase() === "pending"
                                                                            ? COLORS.SEABUCKTHORN
                                                                            : selectedOrder.status.toLowerCase() === "in-progress"
                                                                                ? COLORS.YELLOW
                                                                                : COLORS.GALLERY,
                                                                color: COLORS.BLACK,
                                                            }}
                                                        >
                                                            {selectedOrder.status}
                                                        </span>
                                                    </Typography>
                                                    <Typography>
                                                        <strong>Receiver Mobile: </strong> {selectedOrder.receiverMobile}
                                                    </Typography>
                                                    <Typography>
                                                        <>
                                                            <strong>Drop Off Time: </strong>
                                                            {selectedOrder.dropOffTime ? formatUTCFixed(selectedOrder.dropOffTime) : "-"}
                                                        </>
                                                    </Typography>
                                                    <Typography>
                                                        <>
                                                            <strong>Pick Up Time: </strong>
                                                            {selectedOrder.pickUpTime ? formatUTCFixed(selectedOrder.pickUpTime) : "-"}
                                                        </>

                                                    </Typography>
                                                    <Typography>
                                                        <strong>Created At: </strong>
                                                        {selectedOrder.createdAt ? formatUTCFixed(selectedOrder.createdAt) : "-"}
                                                    </Typography>
                                                    <Typography>
                                                        <strong>Updated At: </strong>
                                                        {selectedOrder.updatedAt ? formatUTCFixed(selectedOrder.updatedAt) : "-"}
                                                    </Typography>
                                                    <Typography>
                                                        <strong>Total Amount: </strong>{" "}
                                                        {selectedOrder.payments?.length ? (() => {

                                                            // Approved = BoxPay (approved) + PhonePe (completed) BUT not refunded
                                                            const approvedTotal = selectedOrder.payments
                                                                .filter(p =>
                                                                    ["approved", "completed"].includes(p.status?.toLowerCase()) &&
                                                                    !p.refundOperationId
                                                                )
                                                                .reduce((sum, p) => sum + (p.amount || 0), 0)

                                                            // Refunded = ANY payment having refundOperationId
                                                            const refundedTotal = selectedOrder.payments
                                                                .filter(p => p.refundOperationId)
                                                                .reduce((sum, p) => sum + (p.amount || 0), 0)

                                                            const netTotal = approvedTotal - refundedTotal

                                                            return `₹ ${netTotal > 0 ? netTotal : 0}`

                                                        })() : (
                                                            "No Payments"
                                                        )}
                                                    </Typography>

                                                    {/* NOTES SECTION */}
                                                    <Box gridColumn="span 2" mt={2}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            fontWeight="600"
                                                            mb={2}
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1,
                                                                color: COLORS.BLACK
                                                            }}
                                                        >
                                                            <Pencil size={18} /> Notes & Activity
                                                        </Typography>

                                                        {selectedOrder.notes && selectedOrder.notes.length > 0 ? (
                                                            <Box
                                                                sx={{
                                                                    maxHeight: selectedOrder.notes.length > 3 ? 320 : "auto",
                                                                    overflowY: selectedOrder.notes.length > 3 ? "auto" : "visible",
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                    gap: 1.5,
                                                                    pr: selectedOrder.notes.length > 3 ? 1 : 0,
                                                                    "&::-webkit-scrollbar": {
                                                                        width: "6px",
                                                                    },
                                                                    "&::-webkit-scrollbar-track": {
                                                                        background: COLORS.ASH,
                                                                        borderRadius: "10px",
                                                                    },
                                                                    "&::-webkit-scrollbar-thumb": {
                                                                        background: COLORS.ASH,
                                                                        borderRadius: "10px",
                                                                        "&:hover": {
                                                                            background: COLORS.DOVEGRAY,
                                                                        },
                                                                    },
                                                                }}
                                                            >
                                                                {selectedOrder.notes
                                                                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                                                                    .map((note, index) => (
                                                                        <Box
                                                                            key={index}
                                                                            sx={{
                                                                                border: `1px solid ${COLORS.WHITE}`,
                                                                                borderRadius: "8px",
                                                                                p: 2,
                                                                                position: "relative",
                                                                                transition: "all 0.2s ease",
                                                                                "&:hover": {
                                                                                    boxShadow: `0 2px 8px ${COLORS.BLACK}`,
                                                                                    borderColor: COLORS.BLACK,
                                                                                },
                                                                                "&::before": {
                                                                                    content: '""',
                                                                                    position: "absolute",
                                                                                    left: 0,
                                                                                    top: 0,
                                                                                    bottom: 0,
                                                                                    width: "4px",
                                                                                    background:
                                                                                        index === 0
                                                                                            ? COLORS.ACTIVE
                                                                                            : COLORS.DOVEGRAY,
                                                                                    borderTopLeftRadius: "8px",
                                                                                    borderBottomLeftRadius: "8px",
                                                                                },
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                sx={{
                                                                                    display: "flex",
                                                                                    justifyContent: "space-between",
                                                                                    alignItems: "flex-start",
                                                                                    mb: 1,
                                                                                }}
                                                                            >
                                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

                                                                                    <Box>
                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            sx={{
                                                                                                color: COLORS.BLACK,
                                                                                                display: "flex",
                                                                                                alignItems: "center",
                                                                                                gap: 1.5,
                                                                                                mb: 1
                                                                                            }}
                                                                                        >
                                                                                            <CalendarDays size={18} />
                                                                                            {formatUTCFixed(note.date)}
                                                                                        </Typography>
                                                                                        <Typography variant="body2" fontWeight="600" sx={{
                                                                                            color: COLORS.BLACK, gap: 0.5,
                                                                                            px: 1.5,
                                                                                            py: 0.5,
                                                                                        }}>
                                                                                            {note.addedBy || "Unknown"}
                                                                                        </Typography>
                                                                                    </Box>
                                                                                </Box>
                                                                                {index === 0 && (
                                                                                    <Box
                                                                                        sx={{
                                                                                            background: COLORS.GREEN,
                                                                                            color: COLORS.WHITE,
                                                                                            px: 1.5,
                                                                                            py: 0.5,
                                                                                            borderRadius: "12px",
                                                                                            fontSize: "11px",
                                                                                            fontWeight: "600",
                                                                                        }}
                                                                                    >
                                                                                        Latest
                                                                                    </Box>
                                                                                )}
                                                                            </Box>
                                                                            <Box
                                                                                sx={{
                                                                                    mt: 1,
                                                                                    pl: 1,
                                                                                    pr: 1,
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{
                                                                                        color: COLORS.BLACK,
                                                                                        lineHeight: 1.2
                                                                                    }}
                                                                                >
                                                                                    {note.note}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    ))}
                                                            </Box>
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    textAlign: "center",
                                                                    py: 4,
                                                                    px: 2,
                                                                    borderRadius: "8px",
                                                                    background: COLORS.WHITE,
                                                                }}
                                                            >
                                                                <Pencil size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                                                                <Typography variant="body2" color="text.secondary">
                                                                    No Notes Available For This Order.
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>

                                                    <Box gridColumn="span 2">
                                                        <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                                                            Payments
                                                        </Typography>

                                                        {selectedOrder.payments.length > 0 ? (
                                                            <Box
                                                                sx={{
                                                                    maxHeight: selectedOrder.payments.length > 3 ? 250 : "auto",
                                                                    overflowY: selectedOrder.payments.length > 3 ? "auto" : "visible",
                                                                    pr: 1
                                                                }}
                                                            >
                                                                {selectedOrder.payments.map((p, i) => (
                                                                    <Box
                                                                        key={`${p.paymentOrderId}-${i}`}
                                                                        mb={1}
                                                                        p={1}
                                                                        border="1px solid #ccc"
                                                                        borderRadius="4px"
                                                                    >
                                                                        <Typography>
                                                                            <strong>Amount:</strong> ₹ {p.amount}
                                                                        </Typography>

                                                                        <Typography>
                                                                            <strong>Status:</strong> {p.status}
                                                                        </Typography>

                                                                        <Typography>
                                                                            <strong>Transaction ID:</strong> {p.paymentOrderId}
                                                                        </Typography>

                                                                        <Typography>
                                                                            <strong>Date:</strong>{" "}
                                                                            {p.date ? formatUTCFixed(p.date) : "-"}
                                                                        </Typography>

                                                                        {/* Display refundOperationId if exists */}
                                                                        {p.refundOperationId && (
                                                                            <Typography>
                                                                                <strong>Refund Operation ID:</strong> {p.refundOperationId}
                                                                            </Typography>
                                                                        )}

                                                                        {["approved", "completed"].includes(p.status?.toLowerCase()) && !p.refundOperationId && (
                                                                            <Box minWidth="250px" textAlign="right">
                                                                                <Button
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    disabled={isOrderRefunded}
                                                                                    onClick={() =>
                                                                                        setOpenRefundFor(openRefundFor === p.paymentOrderId ? null : p.paymentOrderId)
                                                                                    }
                                                                                >
                                                                                    {isOrderRefunded ? "Refunded" : "Refund"}
                                                                                </Button>

                                                                                {openRefundFor === p.paymentOrderId && (
                                                                                    <Box
                                                                                        mt={2}
                                                                                        p={2}
                                                                                        border="1px solid #ddd"
                                                                                        borderRadius={2}
                                                                                        bgcolor="#fafafa"
                                                                                        display="flex"
                                                                                        flexDirection="column"
                                                                                        gap={2}
                                                                                    >
                                                                                        <TextField
                                                                                            select
                                                                                            fullWidth
                                                                                            label="Refund Reason"
                                                                                            size="small"
                                                                                            value={refundReason}
                                                                                            onChange={(e) => setRefundReason(e.target.value as RefundReason)}
                                                                                        >
                                                                                            <MenuItem value={RefundReason.REQUESTED_BY_CUSTOMER}>
                                                                                                Requested by Customer
                                                                                            </MenuItem>
                                                                                            <MenuItem value={RefundReason.DUPLICATE_ORDER}>
                                                                                                Duplicate Order
                                                                                            </MenuItem>
                                                                                        </TextField>

                                                                                        <TextField
                                                                                            fullWidth
                                                                                            label="Custom Refund Reason"
                                                                                            size="small"
                                                                                            multiline
                                                                                            minRows={2}
                                                                                            value={customRefundReason}
                                                                                            onChange={(e) => setCustomRefundReason(e.target.value)}
                                                                                        />

                                                                                        <Button
                                                                                            color="primary"
                                                                                            variant="contained"
                                                                                            disabled={isOrderRefunded || loadingRefundId === p.paymentOrderId}
                                                                                            onClick={() => handleRefund(p.paymentOrderId, p.amount)}
                                                                                        >
                                                                                            {loadingRefundId === p.paymentOrderId ? "Processing..." : "Confirm Refund"}
                                                                                        </Button>
                                                                                    </Box>
                                                                                )}
                                                                            </Box>
                                                                        )}

                                                                        {p.refundOperationId && (
                                                                            <>
                                                                                {refundStatusMap[p.refundOperationId] === "COMPLETED" ? (
                                                                                    <Box
                                                                                        sx={{
                                                                                            display: "inline-flex",
                                                                                            alignItems: "center",
                                                                                            gap: 0.5,
                                                                                            mt: 1,
                                                                                            px: 1.5,
                                                                                            py: 0.5,
                                                                                            backgroundColor: "#dcfce7",
                                                                                            border: "1px solid #86efac",
                                                                                            borderRadius: "6px",
                                                                                        }}
                                                                                    >
                                                                                        <svg
                                                                                            width="16"
                                                                                            height="16"
                                                                                            viewBox="0 0 20 20"
                                                                                            fill="none"
                                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                                        >
                                                                                            <path
                                                                                                d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z"
                                                                                                fill="#16a34a"
                                                                                            />
                                                                                        </svg>
                                                                                        <Typography
                                                                                            sx={{
                                                                                                fontSize: "0.875rem",
                                                                                                fontWeight: 600,
                                                                                                color: "#16a34a",
                                                                                            }}
                                                                                        >
                                                                                            REFUNDED
                                                                                        </Typography>
                                                                                    </Box>
                                                                                ) : (
                                                                                    <Button
                                                                                        size="small"
                                                                                        variant="outlined"
                                                                                        color="primary"
                                                                                        sx={{ mt: 1 }}
                                                                                        disabled={loadingRefundStatusId === p.refundOperationId}
                                                                                        onClick={() => handleRefundStatus(p.refundOperationId)}
                                                                                    >
                                                                                        {loadingRefundStatusId === p.refundOperationId ? "Checking..." : "REFUND STATUS"}
                                                                                    </Button>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        ) : (
                                                            <Typography>No payments found.</Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </DialogContent>

                                            <DialogActions>
                                                <Button onClick={() => setSelectedOrder(null)} color="primary" variant="contained">
                                                    Close
                                                </Button>
                                            </DialogActions>
                                        </Dialog>
                                    )}
                                </Box>
                            ))
                        )
                    })()}
                </div>
            ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height={150}>
                    <Typography variant="h6" sx={{ color: "gray" }}>
                        Click on "Expand All" to view orders
                    </Typography>
                </Box>
            )}

            {totalPages > 1 && (
                <Box display="flex" justifyContent="center" alignItems="center" mt={4} gap={2} flexWrap="wrap">
                    <Button
                        variant="outlined"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        sx={{
                            minWidth: 100,
                            textTransform: "capitalize",
                            color: COLORS.SIDEBAR_BG,
                            borderColor: COLORS.SIDEBAR_BG,
                            "&:hover": { backgroundColor: COLORS.LIGHT_GRAY, borderColor: COLORS.SIDEBAR_BG },
                            "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
                        }}
                    >
                        Previous
                    </Button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <Button
                            key={page}
                            variant={currentPage === page ? "contained" : "outlined"}
                            onClick={() => handlePageChange(page)}
                            sx={{
                                minWidth: 40,
                                height: 40,
                                color: currentPage === page ? "white" : COLORS.SIDEBAR_BG,
                                backgroundColor: currentPage === page ? COLORS.SIDEBAR_BG : "transparent",
                                borderColor: COLORS.SIDEBAR_BG,
                                "&:hover": {
                                    backgroundColor: currentPage === page ? COLORS.SIDEBAR_BG : COLORS.LIGHT_GRAY,
                                    borderColor: COLORS.SIDEBAR_BG,
                                },
                            }}
                        >
                            {page}
                        </Button>
                    ))}

                    <Button
                        variant="outlined"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        sx={{
                            minWidth: 100,
                            color: COLORS.SIDEBAR_BG,
                            textTransform: "capitalize",
                            borderColor: COLORS.SIDEBAR_BG,
                            "&:hover": { backgroundColor: COLORS.LIGHT_GRAY, borderColor: COLORS.SIDEBAR_BG },
                            "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
                        }}
                    >
                        Next
                    </Button>

                    <Typography variant="body2" sx={{ color: COLORS.BLACK, ml: 2 }}>
                        Page {currentPage} of {totalPages}
                    </Typography>
                </Box>
            )}

            <Dialog
                open={updateSuccessOpen}
                onClose={() => setUpdateSuccessOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "64px",
                            height: "64px",
                            borderRadius: "50%",
                            backgroundColor: COLORS.LIGHT_GREEN,
                            margin: "0 auto 16px auto"
                        }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-8 h-8"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="white"
                            strokeWidth={2}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                    </div>

                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        Amount Refund completed successfully.
                    </Typography>
                </DialogContent>
            </Dialog>
        </Box>
    )
}

export default FranchiseOrders